import React from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { reverse } from 'named-urls'
import {
  Download as DownloadIcon,
  Eye as EyeIcon,
  Edit as EditIcon,
  Power as PowerIcon,
  Search as SearchIcon,
  Trash as TrashIcon,
} from 'react-feather'
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Hidden,
  InputAdornment,
  TablePagination,
  TextField,
  Tooltip,
} from '@material-ui/core'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/Table'
import { MenuButton, Permitted } from 'components'

import { routes } from 'Routes'
import helpers from 'helpers'
import theme from 'theme'

import constants from 'constants/index'

import useStyles from './styles'

const MyDocumentsTable = ({
  count,
  documents,
  isLoading,
  isSearching,
  searchInput,
  handleSearchValue,
  handleOpenInactiveDialog,
  handleOpenDeleteDialog,
  handleDownloadDocumentCopy,
  pagination = {},
}) => {
  const classes = useStyles()
  const history = useHistory()

  const {
    CHIP_STATUS_COLOR,
    INACTIVE_STATUS_ID,
    EXPIRED_STATUS_ID,
    LINK_CONTENT_KIND,
    TRANSLATION_CATEGORY,
    ATTACHMENT_CONTENT_KIND,
  } = constants.documents
  const { MAIN_STEP_7 } = constants.tours.documents

  const { PRIVACY_POLICIES } = constants.permissions

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    pagination

  const handleEditRedirection = (documentId) =>
    history.push(reverse(routes.documents.edit, { documentId }))

  const handleShowRedirection = (documentId) =>
    history.push(reverse(routes.documents.show, { documentId }))

  return (
    <Box display="flex" flexDirection="column" gridGap={theme.spacing(3)}>
      <TextField
        fullWidth
        placeholder="Buscar por nome ou categoria"
        value={searchInput}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon size={20} />
            </InputAdornment>
          ),
          endAdornment: isSearching && (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ),
        }}
        variant="outlined"
        onChange={handleSearchValue}
      />
      <Card>
        <PerfectScrollbar
          options={{ wheelPropagation: false, useBothWheelAxes: true }}
        >
          <Table
            emptyMessage="Nenhum documento foi encontrado."
            isLoading={isLoading}
            size="small"
            noWrap
          >
            <TableHead>
              <TableRow withStatus={!isEmpty(documents)}>
                <TableCell width="5%">ID</TableCell>
                <TableCell width="30%">Nome do documento</TableCell>
                <TableCell width="12%">Categoria</TableCell>
                <TableCell width="12%">Criado por</TableCell>
                <TableCell width="13%">Aprovado por</TableCell>
                <TableCell align="center" width="13%">
                  Última atualização
                </TableCell>
                <TableCell align="center" width="10%">
                  Versão / Revisão
                </TableCell>
                <TableCell align="right" width="5%">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents?.map((document) => {
                const isInactive = document?.status === INACTIVE_STATUS_ID
                const isAttachment =
                  document?.contentKind === ATTACHMENT_CONTENT_KIND
                const isLinkContent =
                  document?.contentKind === LINK_CONTENT_KIND
                const statusColor = CHIP_STATUS_COLOR[document?.status]
                const categoryLabel =
                  TRANSLATION_CATEGORY[document?.category] || document?.category
                const isExpired = document?.status === EXPIRED_STATUS_ID

                return (
                  <TableRow key={document?.id} statusColor={statusColor}>
                    <TableCell width="5%">{document?.id}</TableCell>
                    <TableCell width="18%" applyNoWrap disableTooltip={false}>
                      {document?.title}
                    </TableCell>
                    <TableCell width="12%" applyNoWrap disableTooltip={false}>
                      {categoryLabel}
                    </TableCell>
                    <TableCell width="12%" applyNoWrap disableTooltip={false}>
                      {document?.author?.name || 'Sistema'}
                    </TableCell>
                    <TableCell width="13%">
                      {isEmpty(document?.approvedBy) ? (
                        '-'
                      ) : (
                        <Box className={classes.approvedByContainer}>
                          <Tooltip
                            title={document.approvedBy[0].email}
                            placement="bottom-start"
                          >
                            <Box className={classes.approvedByEmail}>
                              {document.approvedBy[0].email}
                            </Box>
                          </Tooltip>
                          {document.approvedBy.length > 1 && (
                            <Tooltip
                              title={document.approvedBy
                                .slice(1)
                                .map((approvedUser) => approvedUser.email)
                                .join(', ')}
                              placement="bottom-start"
                            >
                              <Chip
                                className={classes.moreChip}
                                label={`+${document.approvedBy.length - 1}`}
                                size="small"
                              />
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center" width="13%">
                      {document?.updatedAt
                        ? helpers.formatters.ptBrFormatDateTime(
                            document?.updatedAt,
                          )
                        : '-'}
                    </TableCell>
                    <TableCell align="center" width="10%">
                      {document?.version
                        ? `${document?.version}.${document?.review ?? ''}`
                        : '-'}
                    </TableCell>
                    <TableCell align="right" width="5%">
                      <MenuButton id={MAIN_STEP_7}>
                        <Button
                          color="secondary"
                          fullWidth
                          size="small"
                          startIcon={<EyeIcon size={16} strokeWidth={2.5} />}
                          onClick={() => handleShowRedirection(document?.id)}
                        >
                          Visualizar
                        </Button>
                        {!isLinkContent && (
                          <>
                            <Permitted tag={PRIVACY_POLICIES.CREATE}>
                              <Button
                                color="secondary"
                                fullWidth
                                size="small"
                                startIcon={
                                  <EditIcon size={16} strokeWidth={2.5} />
                                }
                                onClick={() =>
                                  handleEditRedirection(document?.id)
                                }
                              >
                                {isExpired ? 'Revisar' : 'Editar'}
                              </Button>
                            </Permitted>
                            <Permitted tag={PRIVACY_POLICIES.CREATE}>
                              <Button
                                color="secondary"
                                fullWidth
                                size="small"
                                startIcon={
                                  <PowerIcon size={16} strokeWidth={2.5} />
                                }
                                onClick={() =>
                                  handleOpenInactiveDialog(
                                    document?.id,
                                    document?.status,
                                  )
                                }
                              >
                                {isInactive ? 'Ativar' : 'Inativar'}
                              </Button>
                            </Permitted>
                          </>
                        )}

                        {!isAttachment && !isLinkContent && (
                          <Permitted tag={PRIVACY_POLICIES.CREATE}>
                            <Tooltip
                              title={
                                !document?.version
                                  ? 'Este documento ainda não possui uma versão ativa para exportação'
                                  : ''
                              }
                            >
                              <Box>
                                <Button
                                  color="secondary"
                                  fullWidth
                                  size="small"
                                  startIcon={
                                    <DownloadIcon size={16} strokeWidth={2.5} />
                                  }
                                  onClick={() =>
                                    handleDownloadDocumentCopy(document)
                                  }
                                  disabled={!document?.version}
                                >
                                  Exportar
                                </Button>
                              </Box>
                            </Tooltip>
                          </Permitted>
                        )}

                        <Permitted tag={PRIVACY_POLICIES.CREATE}>
                          <Button
                            color="secondary"
                            fullWidth
                            size="small"
                            startIcon={
                              <TrashIcon size={16} strokeWidth={2.5} />
                            }
                            onClick={() =>
                              handleOpenDeleteDialog(
                                document?.id,
                                !!document?.consentFormId,
                              )
                            }
                          >
                            Excluir
                          </Button>
                        </Permitted>
                      </MenuButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </PerfectScrollbar>
        <Box px={2}>
          <TablePagination
            component="div"
            count={count}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            page={page - 1}
            rowsPerPage={perPage}
            rowsPerPageOptions={[6, 12, 24, 100]}
            labelRowsPerPage={<Hidden smDown>Por página</Hidden>}
            nextIconButtonProps={{ size: 'small' }}
            backIconButtonProps={{ size: 'small' }}
          />
        </Box>
      </Card>
    </Box>
  )
}

MyDocumentsTable.propTypes = {
  documents: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  isSearching: PropTypes.bool,
  searchInput: PropTypes.string,
  handleSearchValue: PropTypes.func.isRequired,
  handleOpenInactiveDialog: PropTypes.func.isRequired,
  handleOpenDeleteDialog: PropTypes.func.isRequired,
  handleDownloadDocumentCopy: PropTypes.func.isRequired,
}

export default MyDocumentsTable
